import React, { useMemo } from 'react';

// utils
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_LONG } from '../../../util/types';
import {
  constructQueryParamName,
  pickInitialValuesForFieldSelectTree,
  parseSelectFilterOptions,
} from '../../../util/search';
import { ActiveFilters } from '../../../components';

import css from '../SearchPage.module.css';

const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

const ActiveFiltersMaybe = props => {
  const { initialValues, filters, getHandleChangedValueFn, marketplaceCurrency, ...rest } = props;
  const currency = marketplaceCurrency;

  const activeFilters = useMemo(() => {
    const results = [];

    // Helper: Process enum filters
    const processEnumFilters = (config, queryParamNames, value, newObject) => {
      const { filterConfig, enumOptions } = config;
      const { filterType } = filterConfig;

      if (filterType === 'SelectSingleFilter') {
        return;
      }

      const queryParamName = getQueryParamName(queryParamNames);
      const selectedOptions = value[queryParamName]
        ? parseSelectFilterOptions(value[queryParamName])
        : [];
      const filterValue = enumOptions.filter(({ option }) => selectedOptions.includes(option));

      if (filterValue.length) {
        filterValue.forEach(({ option, label }) => {
          results.push({
            ...newObject,
            label,
            option,
            queryParamName,
          });
        });
      } else {
        results.push({
          ...newObject,
          queryParamName,
          ...filterValue,
        });
      }
    };

    // Helper: Process non-enum filters
    const processNonEnumFilters = (schemaType, value, newObject, key) => {
      if (schemaType === 'price' && value?.price) {
        const [min, max] = value.price.split(',');
        results.push({
          ...newObject,
          queryParamName: key,
          label: `${min}-${max} ${currency}`,
          option: 'price',
        });
      } else {
        results.push({
          queryParamName: key,
          ...newObject,
          ...value,
        });
      }
    };

    // Iterate over filters
    filters
      .filter(filter => filter.schemaType !== 'category')
      .forEach(config => {
        const { key, schemaType, scope } = config;
        const name = key.replace(/\s+/g, '-');
        const newObject = { key, schemaType, config, name };

        const queryParamNames =
          schemaType === 'price' ? [key] : [constructQueryParamName(key, scope)];
        const value = initialValues(queryParamNames, false);

        // Skip if no values exist
        if (!Object.values(value).length) return;

        if ([SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_LONG].includes(schemaType)) {
          processEnumFilters(config, queryParamNames, value, newObject);
        } else {
          processNonEnumFilters(schemaType, value, newObject, key);
        }
      });

    return results;
  }, [filters, initialValues, currency]);

  const onSubmit = (name, option, filter) => {
    const values = activeFilters
      .filter(filter => filter.name === name && filter.option !== option)
      .map(v => v.option);
    const handleChangedValue = getHandleChangedValueFn(true);
    handleChangedValue(format(values, filter.queryParamName, filter.schemaType));
  };

  return (
    <div className={css.activeFilters}>
      <ActiveFilters {...rest} filters={activeFilters} onSubmit={onSubmit} />
    </div>
  );
};

export default ActiveFiltersMaybe;
